import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Plus, Filter } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import { Badge } from '@/app/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { usePayrollRuns, useUnassignedPayrollAdjustments } from '@/hooks/use-payroll';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { formatMoney } from '@/lib/currency';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { PayrollRunStatus } from '@/lib/types';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  finalized: 'bg-green-100 text-green-800',
  exported: 'bg-purple-100 text-purple-800',
};
const MINIMUM_TWO_FRACTION_DIGITS = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
} satisfies Intl.NumberFormatOptions;

export function PayrollRunsListPage() {
  const orgCurrency = useOrgCurrency();
  const timeZone = useOrgTimeZone();
  const { canManagePayroll } = usePermissions();
  const [statusFilter, setStatusFilter] = useState<PayrollRunStatus | ''>('');

  const { data: runsPage, isLoading } = usePayrollRuns({
    status: statusFilter || undefined,
  });
  const runs = runsPage?.results ?? [];
  const runCount = runsPage?.count ?? 0;
  const { data: unassignedAdjustments = [] } = useUnassignedPayrollAdjustments();

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payroll Runs</h1>
          <p className="text-gray-600 mt-1">
            Calculate and export payroll for pay periods
            {!isLoading && runsPage && (
              <span data-testid="payroll-runs-count"> &middot; {runCount} run{runCount !== 1 ? 's' : ''}</span>
            )}
          </p>
        </div>
        {canManagePayroll && (
          <Button asChild>
            <Link to="/payroll/runs/new">
              <Plus className="h-4 w-4 mr-2" />
              New Payroll Run
            </Link>
          </Button>
        )}
      </div>

      {unassignedAdjustments.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>Unassigned prior-period adjustments</AlertTitle>
          <AlertDescription>
            <div className="space-y-1">
              {unassignedAdjustments.map((adjustment) => (
                <div key={adjustment.id}>
                  {adjustment.worker_name} · {adjustment.field_label} {adjustment.delta} ·
                  Run #{adjustment.original_run} ({adjustment.original_run_pay_period_name}) ·
                  {adjustment.reason}
                </div>
              ))}
              <p>These will be attached automatically to the next pay period run you create.</p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v as PayrollRunStatus | '')}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
                <SelectItem value="exported">Exported</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payroll Runs List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading payroll runs...</div>
          ) : runs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Calculator className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No payroll runs found</p>
              {canManagePayroll && (
                <Button asChild className="mt-4" variant="outline">
                  <Link to="/payroll/runs/new">Create your first payroll run</Link>
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {runs.map((run) => (
                <Link
                  key={run.id}
                  to={`/payroll/runs/${run.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{run.pay_period_name}</p>
                    <p className="text-sm text-gray-500">
                      {run.line_item_count} tech{run.line_item_count !== 1 ? 's' : ''} &middot;
                      Created {formatDateTimeInTimeZone(run.created_at, timeZone, { dateStyle: 'medium' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-900">
                      {formatMoney(run.grand_total, orgCurrency, MINIMUM_TWO_FRACTION_DIGITS)}
                    </span>
                    <Badge className={statusColors[run.status] ?? 'bg-gray-100 text-gray-800'}>
                      {run.status}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
